import type { CSSProperties } from 'react';
import { Badge } from '@/components/ui/primitives';
import { cn } from '@/lib/utils';
import type { BrandKit, Channel, ContentPage, Format } from '@/types/social';
import { aspectCss, channelLabel } from '@/data/studio-layouts';
import { resolveMirrorMedia, stylingRelabelText, stylingRelabelVisible } from '@/data/social-mirror';

/* ----------------------------------------------------------------------------
 * Channel-framed content preview. A CSS-framed text/media render at the true
 * aspect ratio — NEVER dangerouslySetInnerHTML (all text is a React child, so
 * it is encode-on-output by construction). NO freeform canvas: layout comes
 * from the page's layout_key, colours/fonts ONLY from the Brand Kit, alignment/
 * size from the constrained enums. Pexels attribution is always shown.
 * -------------------------------------------------------------------------- */

/**
 * Owner render decision: FB/LinkedIn (and X / generic single) show a PLAIN image
 * (media + logo only, NO baked caption text). Instagram — and ANY carousel
 * format — bake headline/body/hashtags/cta over the media, with a dark scrim for
 * legibility. slots.body stays populated for EVERY platform; this is purely a
 * RENDER choice, not a data change.
 */
export function isBakedPreview(channel: Channel, format: Format): boolean {
  if (format === 'carousel') return true;
  return channel === 'instagram';
}

const FONT_STACKS: Record<string, string> = {
  'Source Serif 4': "'Source Serif 4', Georgia, serif",
  Inter: "'Inter', system-ui, sans-serif",
  'system-ui': 'system-ui, sans-serif',
  Georgia: 'Georgia, serif',
  Arial: 'Arial, Helvetica, sans-serif',
};

function alignClass(align: ContentPage['align']): string {
  return align === 'center' ? 'items-center text-center' : align === 'end' ? 'items-end text-right' : 'items-start text-left';
}
function sizeClass(size: ContentPage['textSize']): string {
  return size === 's' ? 'text-sm' : size === 'l' ? 'text-2xl' : 'text-lg';
}

/**
 * Non-dismissable, text-based relabel shown over a media composition. The Studio
 * frame is STYLING ONLY — the baked headline/CTA/scrim are never rasterised, so
 * the post ships as the raw image + a separate caption (see PublishMirror /
 * SocialPostModel::fromDraft, Option A). This label stops a user leaving the
 * editor believing the composed graphic is what publishes. Rendered for EVERY
 * baked/composed layout (even media-less — the composed graphic never ships)
 * and for any frame with media (which ships raw); visibility + wording come
 * from the single mirror model (stylingRelabelVisible / stylingRelabelText).
 */
function StylingPreviewLabel({ hasMedia }: { hasMedia: boolean }) {
  return (
    <div className="absolute left-2 top-2 z-20 max-w-[calc(100%-1rem)] rounded bg-black/75 px-2 py-1 text-[11px] font-medium leading-tight text-white shadow-sm ring-1 ring-white/20">
      {stylingRelabelText(hasMedia)}
    </div>
  );
}

export function ContentPreview({
  channel,
  aspect,
  page,
  brand,
  workspaceName = 'Your brand',
  format = 'single',
}: {
  channel: Channel;
  aspect: 'landscape' | 'portrait' | 'square';
  page: ContentPage;
  brand: BrandKit | null;
  workspaceName?: string;
  format?: Format;
}) {
  const ratio = aspectCss(channel, aspect);
  const bg = brand?.colorSecondary || '#1B1B1A';
  const accent = brand?.colorPrimary || '#A5622E';
  const font = FONT_STACKS[brand?.fonts?.[0] ?? 'Source Serif 4'] ?? FONT_STACKS['Source Serif 4'];
  const frameStyle: CSSProperties = { aspectRatio: String(ratio), background: bg, fontFamily: font };
  const s = page.slots;
  // Single-source media resolution: the SAME helper the publish mirror uses
  // (resolveMirrorMedia == fromDraft primaryMedia), so the frame, the on-frame
  // relabel and the "What will post" panel can never disagree about the asset.
  const mirrorMedia = resolveMirrorMedia(s.media);
  const mediaUrl = mirrorMedia?.url ?? null;
  const mediaIsVideo = mirrorMedia?.kind === 'video';
  const justify = page.align === 'center' ? 'center' : page.align === 'end' ? 'flex-end' : 'flex-start';
  const baked = isBakedPreview(channel, format);
  const overlayOpacity = Math.min(100, Math.max(0, s.overlay ?? 45)) / 100;

  return (
    <div className="w-full">
      <div
        className={cn('relative flex flex-col justify-between overflow-hidden rounded-lg border border-border-default p-5', alignClass(page.align))}
        style={frameStyle}
        role="img"
        aria-label={`${channelLabel(channel)} ${aspect} preview`}
      >
        {stylingRelabelVisible(baked, mirrorMedia) ? <StylingPreviewLabel hasMedia={mirrorMedia !== null} /> : null}
        {mediaUrl ? (
          // P0 recovery: render the FRESH signed rendition (largeUrl) resolved on
          // read, falling back to the persisted thumbnail. A VIDEO renders as a
          // <video> (poster = first frame) so it never degrades to a black <img>.
          mediaIsVideo ? (
            <video
              src={mediaUrl}
              className="absolute inset-0 h-full w-full object-cover opacity-90"
              muted
              loop
              playsInline
              autoPlay
              preload="metadata"
              aria-label={s.media?.attribution || 'selected media'}
            />
          ) : (
            <img
              src={mediaUrl}
              alt={s.media?.attribution || 'selected media'}
              className="absolute inset-0 h-full w-full object-cover opacity-90"
            />
          )
        ) : null}
        {mediaUrl && baked ? (
          // Legibility scrim (owner MVP: SOLID dark, opacity-only — no gradient).
          // Sits ABOVE the media (z-[1]) and BELOW the text (z-10).
          <div className="absolute inset-0 z-[1] bg-black" style={{ opacity: overlayOpacity }} aria-hidden="true" />
        ) : null}
        <div className="relative z-10 flex w-full items-center justify-between">
          {s.logo ? (
            brand?.logoAssetId ? (
              <span className="rounded bg-black/40 px-2 py-1 text-xs font-medium text-white">Logo</span>
            ) : (
              <span className="text-sm font-semibold text-white/90" style={{ fontFamily: font }}>{workspaceName}</span>
            )
          ) : <span />}
        </div>
        {baked ? (
        <div className={cn('relative z-10 flex w-full flex-col gap-2', alignClass(page.align))}>
          {s.headline ? (
            <h3 className={cn('font-semibold leading-tight text-white drop-shadow', sizeClass(page.textSize))} style={{ fontFamily: font }}>
              {s.headline}
            </h3>
          ) : null}
          {s.body ? <p className="max-w-[90%] text-sm leading-snug text-white/90 drop-shadow">{s.body}</p> : null}
          {s.hashtags && s.hashtags.length > 0 ? (
            <p className="text-xs text-white/80">{s.hashtags.map((h) => (h.startsWith('#') ? h : `#${h}`)).join(' ')}</p>
          ) : null}
          <div className="mt-1 flex w-full items-center gap-2" style={{ justifyContent: justify }}>
            {s.cta ? (
              <span className="rounded-full px-3 py-1 text-xs font-semibold text-white" style={{ background: accent }}>{s.cta}</span>
            ) : null}
            {s.contact ? <span className="text-xs text-white/80">{brand?.contact || 'contact@yourbrand.com'}</span> : null}
          </div>
        </div>
        ) : null}
      </div>
      {s.media?.attribution ? (
        <p className="mt-2 text-[11px] text-text-tertiary">{s.media.attribution}</p>
      ) : null}
    </div>
  );
}

/** Carousel thumbnail strip — reorder is list-based (up/down), NOT a canvas. */
export function CarouselStrip({
  pages,
  active,
  onSelect,
  onMove,
  onRemove,
}: {
  pages: ContentPage[];
  active: number;
  onSelect: (idx: number) => void;
  onMove: (idx: number, dir: -1 | 1) => void;
  onRemove: (idx: number) => void;
}) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-1">
      {pages.map((p, i) => (
        <div
          key={i}
          className={cn(
            'flex min-w-[92px] flex-col gap-1 rounded-md border p-2 text-left',
            i === active ? 'border-ai bg-ai/5' : 'border-border-subtle bg-surface',
          )}
        >
          <button onClick={() => onSelect(i)} className="text-left">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-medium text-text-primary">Page {i + 1}</span>
              <Badge tone="neutral">{p.layoutKey.replace('carousel-', '')}</Badge>
            </div>
            <p className="mt-1 line-clamp-2 text-[10px] text-text-secondary">{p.slots.headline || p.slots.body || 'Empty'}</p>
          </button>
          <div className="flex items-center justify-between gap-1">
            <button onClick={() => onMove(i, -1)} disabled={i === 0} className="rounded px-1 text-xs text-text-secondary disabled:opacity-30" aria-label="Move earlier">&uarr;</button>
            <button onClick={() => onMove(i, 1)} disabled={i === pages.length - 1} className="rounded px-1 text-xs text-text-secondary disabled:opacity-30" aria-label="Move later">&darr;</button>
            <button onClick={() => onRemove(i)} disabled={pages.length <= 1} className="rounded px-1 text-xs text-danger disabled:opacity-30" aria-label="Remove page">&times;</button>
          </div>
        </div>
      ))}
    </div>
  );
}
